import React from 'react';
import { useItemLastMovements } from '../hooks/useMovements';
import { MapPin, ArrowRight, Clock, User, Package } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import LoadingSpinner from './LoadingSpinner';

interface MovementHistoryProps {
  itemId: number;
  compact?: boolean;
}

const MovementHistory: React.FC<MovementHistoryProps> = ({ itemId, compact = false }) => {
  const { data: movements, isLoading, error } = useItemLastMovements(itemId);

  const getLocationLabel = (movement: any) => {
    // Nouvelle localisation
    const newLocation = movement.nouvelle_zone_id 
      ? `Zone ${movement.nouvelle_zone_id}` 
      : movement.nouvelle_box_id 
        ? `Box ${movement.nouvelle_box_id}` 
        : 'Non défini';

    // Ancienne localisation
    const oldLocation = movement.ancienne_zone_id 
      ? `Zone ${movement.ancienne_zone_id}` 
      : movement.ancienne_box_id 
        ? `Box ${movement.ancienne_box_id}` 
        : 'Non défini';

    return { newLocation, oldLocation };
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm p-2">
        Erreur lors du chargement de l'historique
      </div>
    );
  }

  if (!movements || movements.length === 0) {
    return (
      <div className="text-gray-500 text-sm italic p-2">
        Aucun déplacement enregistré
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2 mb-2">
          <MapPin className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-900">
            Derniers Emplacements ({movements.length})
          </h4>
        </div>

        <div className="space-y-2">
          {movements.map((movement, index) => {
            const { newLocation, oldLocation } = getLocationLabel(movement);
            return (
              <div 
                key={movement.id} 
                className="flex items-start space-x-2 p-2 bg-gray-50 rounded-md text-xs"
              >
                <span className="flex-shrink-0 font-semibold text-gray-600">
                  {index + 1}.
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1 mb-1">
                    <span className="text-gray-600 truncate">{oldLocation}</span>
                    <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <span className="text-blue-600 font-medium truncate">{newLocation}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>{format(new Date(movement.date_mouvement), 'dd/MM/yyyy HH:mm', { locale: fr })}</span>
                  </div>
                  {movement.utilisateur_nom && (
                    <div className="flex items-center space-x-1 text-gray-500 mt-1">
                      <User className="h-3 w-3" />
                      <span>{movement.utilisateur_nom}</span>
                    </div>
                  )}
                  {movement.raison && (
                    <p className="text-gray-600 italic mt-1">
                      "{movement.raison}"
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center space-x-2 mb-4">
        <MapPin className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Historique des Déplacements
        </h3>
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          {movements.length} mouvement{movements.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-3">
        {movements.map((movement, index) => {
          const { newLocation, oldLocation } = getLocationLabel(movement);
          const isRecent = index === 0;

          return (
            <div 
              key={movement.id} 
              className={`p-3 rounded-lg border ${
                isRecent 
                  ? 'border-blue-200 bg-blue-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isRecent 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-400 text-white'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-gray-700 truncate">{oldLocation}</span>
                    <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-blue-600 font-semibold truncate">{newLocation}</span>
                  </div>
                </div>
                {isRecent && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full flex-shrink-0">
                    Actuel
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 text-sm text-gray-600 ml-8">
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{format(new Date(movement.date_mouvement), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
                </div>
                
                {movement.utilisateur_nom && (
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{movement.utilisateur_nom}</span>
                  </div>
                )}
                
                {movement.source && (
                  <div className="flex items-center space-x-1">
                    <Package className="h-4 w-4" />
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                      {movement.source}
                    </span>
                  </div>
                )}
              </div>

              {movement.raison && (
                <div className="mt-2 ml-8 p-2 bg-white rounded border border-gray-200">
                  <p className="text-sm text-gray-700 italic">
                    💬 {movement.raison}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {movements.length >= 5 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Affichage des 5 derniers déplacements. Consultez les rapports pour l'historique complet.
          </p>
        </div>
      )}
    </div>
  );
};

export default MovementHistory;

